import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Info,
  Lightbulb,
  ChevronRight,
  Activity,
  BarChart3,
  Clock,
  Zap,
} from 'lucide-react';
import type { PersonalInsight } from '@/services/aiService';
import { cn } from '@/lib/utils';

interface PersonalInsightsCardProps {
  insights: PersonalInsight[];
  onViewDetails?: () => void;
}

// Type ordering and labels for grouping insights by type (per document spec)
const TYPE_CONFIG: Record<PersonalInsight['type'], { label: string; icon: React.ReactNode; order: number }> = {
  'workload': { label: 'Utilization & Workload', icon: <Activity className="h-4 w-4" />, order: 1 },
  'burnout-risk': { label: 'Anomaly & Risk', icon: <AlertTriangle className="h-4 w-4" />, order: 2 },
  'pattern': { label: 'Patterns & Project Mix', icon: <BarChart3 className="h-4 w-4" />, order: 3 },
  'efficiency': { label: 'Submission Behaviour', icon: <Clock className="h-4 w-4" />, order: 4 },
  'productivity': { label: 'Productivity', icon: <Zap className="h-4 w-4" />, order: 5 },
};

export const PersonalInsightsCard: React.FC<PersonalInsightsCardProps> = ({
  insights,
  onViewDetails,
}) => {
  if (insights.length === 0) return null;

  const getTrendIcon = (trend?: PersonalInsight['trend']) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-3 w-3" />;
      case 'down':
        return <TrendingDown className="h-3 w-3" />;
      case 'stable':
        return <Minus className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getSeverityColor = (severity?: PersonalInsight['severity']) => {
    switch (severity) {
      case 'critical':
        return 'destructive';
      case 'warning':
        return 'default';
      case 'info':
      default:
        return 'secondary';
    }
  };

  const getSeverityIcon = (severity?: PersonalInsight['severity']) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'warning':
        return <Info className="h-4 w-4 text-orange-500" />;
      case 'info':
      default:
        return <Lightbulb className="h-4 w-4 text-primary" />;
    }
  };

  // Group insights by type (per document spec: "Grouped by insight type")
  const groupedInsights = useMemo(() => {
    const types = Object.keys(TYPE_CONFIG) as PersonalInsight['type'][];
    return types
      .map(type => ({
        type,
        config: TYPE_CONFIG[type],
        insights: insights.filter(i => i.type === type),
      }))
      .filter(group => group.insights.length > 0)
      .sort((a, b) => a.config.order - b.config.order);
  }, [insights]);

  // Separate critical insights for prominent display
  const criticalInsights = insights.filter(i => i.severity === 'critical');
  const hasMultipleGroups = groupedInsights.length > 1;

  const renderInsight = (insight: PersonalInsight, isCritical: boolean = false) => {
    if (isCritical) {
      return (
        <div
          key={insight.id}
          className="p-4 rounded-lg border-2 border-destructive bg-destructive/5"
        >
          <div className="flex items-start gap-3">
            {getSeverityIcon(insight.severity)}
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-sm">{insight.title}</h4>
                {insight.trend && (
                  <Badge variant="outline" className="text-xs">
                    {getTrendIcon(insight.trend)}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{insight.description}</p>
              {insight.metric && (
                <p className="text-xs font-medium text-foreground">{insight.metric}</p>
              )}
              {insight.actionable && (
                <div className="mt-2 p-2 bg-background rounded text-xs">
                  <strong>Action:</strong> {insight.actionable}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={insight.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-accent">
        {getSeverityIcon(insight.severity)}
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-sm">{insight.title}</h4>
            {insight.trend && (
              <Badge variant="outline" className="text-xs">
                {getTrendIcon(insight.trend)}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{insight.description}</p>
          {insight.metric && (
            <Badge variant={getSeverityColor(insight.severity)} className="text-xs">
              {insight.metric}
            </Badge>
          )}
          {insight.actionable && (
            <p className="text-xs text-muted-foreground mt-1">
              <Lightbulb className="h-3 w-3 inline mr-1" />
              {insight.actionable}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Personal Insights</CardTitle>
          </div>
          {onViewDetails && (
            <Button variant="ghost" size="sm" onClick={onViewDetails}>
              View All
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
        <CardDescription>AI-powered analysis of your timesheet patterns</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Critical insights displayed prominently at the top */}
        {criticalInsights.length > 0 && (
          <div className="space-y-3">
            {criticalInsights.map((insight) => renderInsight(insight, true))}
          </div>
        )}

        {/* Insights grouped by type (per document spec) */}
        {groupedInsights.map((group) => {
          // Filter out critical insights as they're shown above
          const nonCriticalInsights = group.insights.filter(i => i.severity !== 'critical');
          if (nonCriticalInsights.length === 0) return null;

          return (
            <div key={group.type} className="space-y-2">
              {/* Show type heading only if there are multiple groups */}
              {hasMultipleGroups && (
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide pt-2">
                  <span className="text-primary">{group.config.icon}</span>
                  {group.config.label}
                </div>
              )}
              <div className="space-y-1">
                {nonCriticalInsights.map((insight) => renderInsight(insight, false))}
              </div>
            </div>
          );
        })}

        {/* View More button - only show if handler is provided */}
        {onViewDetails && insights.length > 4 && (
          <Button variant="outline" size="sm" className="w-full" onClick={onViewDetails}>
            View All {insights.length} Insights
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
